export interface PerfToggleState {
  bloom: boolean;
  heat: boolean;
  pixel: boolean;
  radialBlur: boolean;
  particles: {
    dust: boolean;
    engine: boolean;
    scrape: boolean;
    streaks: boolean;
  };
  dayNight: boolean;
  shadowRig: boolean;
  kerbY: boolean;
  cameraSmooth: boolean;
  physics: {
    substepCap: boolean;
  };
}

const STORAGE_KEY = 'perfDiag.toggles';

export const perfToggles: PerfToggleState = {
  bloom: true,
  heat: true,
  pixel: true,
  radialBlur: true,
  particles: {
    dust: true,
    engine: true,
    scrape: true,
    streaks: true,
  },
  dayNight: true,
  shadowRig: true,
  kerbY: true,
  cameraSmooth: true,
  physics: {
    substepCap: true,
  },
};

export type PerfTogglePath =
  | 'bloom'
  | 'heat'
  | 'pixel'
  | 'radialBlur'
  | 'particles.dust'
  | 'particles.engine'
  | 'particles.scrape'
  | 'particles.streaks'
  | 'dayNight'
  | 'shadowRig'
  | 'kerbY'
  | 'cameraSmooth'
  | 'physics.substepCap';

export const PERF_TOGGLE_PATHS: PerfTogglePath[] = [
  'bloom',
  'heat',
  'pixel',
  'radialBlur',
  'particles.dust',
  'particles.engine',
  'particles.scrape',
  'particles.streaks',
  'dayNight',
  'shadowRig',
  'kerbY',
  'cameraSmooth',
  'physics.substepCap',
];

export function getPerfToggle(path: PerfTogglePath): boolean {
  const [head, tail] = path.split('.') as [keyof PerfToggleState, string | undefined];
  const value = perfToggles[head];
  if (tail && typeof value === 'object') {
    return Boolean((value as Record<string, boolean>)[tail]);
  }
  return Boolean(value);
}

export function setPerfToggle(path: PerfTogglePath, enabled: boolean): void {
  const [head, tail] = path.split('.') as [keyof PerfToggleState, string | undefined];
  const value = perfToggles[head];
  if (tail && typeof value === 'object') {
    (value as Record<string, boolean>)[tail] = enabled;
  } else {
    (perfToggles as unknown as Record<string, boolean>)[head] = enabled;
  }
  persistPerfToggles();
}

export function installPerfToggles(
  search = globalThis.location?.search ?? '',
  storage: Storage | null = globalThis.localStorage ?? null,
): void {
  loadPerfToggles(storage);
  const params = new URLSearchParams(search);
  const off = params.get('off');
  if (off) {
    for (const raw of off.split(',')) {
      const path = raw.trim();
      if (isPerfTogglePath(path)) setToggleWithoutPersist(path, false);
    }
  }
  if (params.get('physics.substepCap') === 'Infinity') {
    setToggleWithoutPersist('physics.substepCap', false);
  }
  persistPerfToggles(storage);
}

function loadPerfToggles(storage: Storage | null = globalThis.localStorage ?? null): void {
  if (!storage) return;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw) as Partial<Record<PerfTogglePath, boolean>>;
    for (const path of PERF_TOGGLE_PATHS) {
      if (typeof saved[path] === 'boolean') setToggleWithoutPersist(path, saved[path]);
    }
  } catch {
    // Bad localStorage should not stop the game from booting.
  }
}

function persistPerfToggles(storage: Storage | null = globalThis.localStorage ?? null): void {
  if (!storage) return;
  try {
    const flat: Partial<Record<PerfTogglePath, boolean>> = {};
    for (const path of PERF_TOGGLE_PATHS) flat[path] = getPerfToggle(path);
    storage.setItem(STORAGE_KEY, JSON.stringify(flat));
  } catch {
    // Storage may be disabled in private contexts.
  }
}

function setToggleWithoutPersist(path: PerfTogglePath, enabled: boolean): void {
  const [head, tail] = path.split('.') as [keyof PerfToggleState, string | undefined];
  const value = perfToggles[head];
  if (tail && typeof value === 'object') {
    (value as Record<string, boolean>)[tail] = enabled;
  } else {
    (perfToggles as unknown as Record<string, boolean>)[head] = enabled;
  }
}

function isPerfTogglePath(path: string): path is PerfTogglePath {
  return (PERF_TOGGLE_PATHS as readonly string[]).includes(path);
}
